/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ページアクセス判定フック。useAuth から PageAccessContext を構築し、
 *  canAccessPage(SSOT) をバインドして返す。サイドバー表示とルートガードが共通利用する
 * @graph-connects useAuth [calls] 権限・ユーザー情報を取得
 * @graph-connects canAccessPage [calls] ページ単位のアクセス可否 SSOT
 * @graph-connects buildPageAccessContext [calls] ユーザー情報から判定文脈を組み立てる
 */
import {
  buildPageAccessContext,
  canAccessPage,
  type PageAccessContext,
  type PageKey,
} from '@cortex/domain-product-innovation-lab-types';
import { useAuth } from './useAuth.js';

/**
 * アクセス判定の文脈を返す。
 *
 * 文脈の組み立て（`isInternalMember` / `isPiDivOrAdmin` / `isAdmin` の導出）は
 * `buildPageAccessContext` が持つ。**API も同じ関数を通して判定する**ので、ここで
 * 条件を書き足すと画面と API がずれる。
 *
 * 画面単位より細かい絞り込み（改善要望の対象画面＝`visiblePilabAreas`）に渡すため、
 * ページ可否とは別に公開している。
 *
 * @returns canAccessPage / visiblePilabAreas に渡す判定文脈
 * @graph-connects useAuth [calls] 権限・ユーザー情報を取得
 * @graph-connects buildPageAccessContext [calls] 判定文脈の組み立て
 */
export function usePageAccessContext(): PageAccessContext {
  const { can, user } = useAuth();
  // can は useAuth が持つものを使う（開発時の権限上書き等がそこに入る）。
  return { ...buildPageAccessContext(user), can };
}

/**
 * ページアクセス判定をまとめて返すフック。
 *
 * @returns isInternalMember, isPiDivOrAdmin と、ページキーを受け取りアクセス可否を返す canAccess 関数
 * @graph-connects usePageAccessContext [calls] 判定文脈
 */
export function usePageAccess(): {
  isInternalMember: boolean;
  /** PI Div 所属 or admin。横断 PMO（pi_div 以外 × pmo）は false。 */
  isPiDivOrAdmin: boolean;
  canAccess: (key: PageKey) => boolean;
} {
  const context = usePageAccessContext();

  return {
    isInternalMember: context.isInternalMember,
    isPiDivOrAdmin: context.isPiDivOrAdmin,
    canAccess: (key: PageKey) => canAccessPage(key, context),
  };
}
